import React, { useState } from 'react';
import { areaSeoContent } from '../data/seoContent';
import { SchoolListing } from '../types/api';
import { ChevronDownIcon, BookOpenIcon, SearchIcon, GraduationCapIcon } from 'lucide-react';
interface SeoContentSectionProps {
  areaStudyId: number;
  topSchool?: SchoolListing;
}
export function SeoContentSection({
  areaStudyId,
  topSchool
}: SeoContentSectionProps) {
  const seoData = areaSeoContent[areaStudyId];
  if (!seoData) return null;
  return <section className="w-full bg-gray-50 border-t border-gray-200 py-12 md:py-16" data-id="element-313">
      <div className="max-w-4xl mx-auto px-4" data-id="element-314">
        {/* SEO Title */}
        <div className="flex items-center gap-3 mb-3" data-id="element-315">
          <div className="bg-[#1a2332] p-2 rounded-lg text-white" data-id="element-316">
            <GraduationCapIcon className="w-5 h-5" data-id="element-317" />
          </div>
          <span className="text-xs font-semibold text-[#3b82f6] uppercase tracking-wider" data-id="element-318">
            Program Guide
          </span>
        </div>

        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-319">
          {seoData.pageTitle}
        </h2>

        <p className="text-base text-gray-600 leading-relaxed mb-10" data-id="element-320">
          {seoData.intro}
        </p>

        {/* Content Sections */}
        <div className="space-y-8 mb-10" data-id="element-321">
          {seoData.sections.map((section, idx) => <div key={idx} data-id="element-322">
              <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-start gap-2" data-id="element-323">
                <SearchIcon className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" data-id="element-324" />
                {section.heading}
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed pl-7" data-id="element-325">
                {section.body}
              </p>
            </div>)}
        </div>

        {/* Inline School Ad */}
        {topSchool && <a href={topSchool.clickURL} target="_blank" rel="noopener noreferrer" className="block bg-gradient-to-r from-gray-50 to-blue-50/80 rounded-2xl p-6 md:p-8 border-2 border-blue-200 mb-10 hover:shadow-lg transition-all group" data-id="element-326">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-5" data-id="element-327">
              <div className="flex flex-col sm:flex-row items-center gap-4 flex-1" data-id="element-328">
                <div className="w-28 h-18 flex items-center justify-center bg-white rounded-xl border border-gray-200 p-3 flex-shrink-0" data-id="element-329">
                  <img src={topSchool.imageURL} alt={`${topSchool.mBrandName} logo`} className="max-w-full max-h-full object-contain" data-id="element-330" />
                </div>
                <div className="text-center sm:text-left" data-id="element-331">
                  <p className="text-base font-bold text-gray-900 mb-1" data-id="element-332">
                    {topSchool.mBrandName}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-333">
                    {topSchool.headContent || 'Accredited Degrees'}
                  </p>
                </div>
              </div>
              <div className="bg-[#6366f1] group-hover:bg-[#4f46e5] text-white font-bold py-3 px-8 rounded-xl transition-colors shadow-sm whitespace-nowrap text-sm" data-id="element-334">
                Visit Site
              </div>
            </div>
          </a>}

        {/* FAQ Section */}
        {seoData.faq.length > 0 && <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8" data-id="element-335">
            <div className="flex items-center gap-2 mb-5" data-id="element-336">
              <BookOpenIcon className="w-5 h-5 text-[#e8594f]" data-id="element-337" />
              <h3 className="text-lg font-bold text-gray-900" data-id="element-338">
                Frequently Asked Questions
              </h3>
            </div>

            <div className="space-y-1" data-id="element-339">
              {seoData.faq.map((item, idx) => <FaqItem key={idx} question={item.question} answer={item.answer} defaultOpen={idx === 0} data-id="element-340" />)}
            </div>
          </div>}
      </div>
    </section>;
}
function FaqItem({
  question,
  answer,
  defaultOpen
}: {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen || false);
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-341">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between py-3 text-left hover:text-[#3b82f6] transition-colors" data-id="element-342">
        <span className="text-sm font-semibold text-gray-900 pr-4" data-id="element-343">
          {question}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-344" />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-40 opacity-100 pb-3' : 'max-h-0 opacity-0'}`} data-id="element-345">
        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-346">{answer}</p>
      </div>
    </div>;
}